import striptags from 'striptags';
import {
    ContentBlockTickets,
    fetchApiData,
    fetchCraftData,
    formatShowTitle,
    getCredits,
    gtm,
    LoadingFilmPage,
    slugifyTitle,
    TicketDatePicker,
    useApi,
    useFilmInfo,
    VideoButtons,
} from 'shared';
import { NextSeo } from 'next-seo';
import {
    Award,
    Body,
    Box,
    Button,
    ColorSchemeProvider,
    Container,
    FavoriteButton,
    Flex,
    Grid,
    Heading,
    Hero,
    HStack,
    HtmlContent,
    IconAdd,
    IconRemove,
    LightTable,
    MediaModal,
    Meta,
    Stack,
    StillsCarousel,
    Tag,
    TextButton,
    TheaterModal,
    useTheaterModal,
    Wrapper,
} from 'designsystem';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetStaticPaths, GetStaticProps } from 'next';
import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import React, { useEffect, useMemo, useState } from 'react';
import fetchProgramUri from '../../../utils/fetchProgramUri';
import prefetchGlobals from '../../../utils/prefetchGlobals';
import FestivalBreadcrumbs from '../../../components/FestivalBreadcrumbs';
import { FilmAlertQuery, FilmAlertQueryVariables, useFilmAlertQuery, useProgramPathQuery } from '../../../gql/cms';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import {
    AlphabetSearchHitsQuery,
    AlphabetSearchHitsQueryVariables,
    FestivalFilmDetailDocument,
    FestivalFilmDetailQuery,
    FestivalFilmDetailQueryVariables,
    TicketAvailabilityStatusEnum,
    useAlphabetSearchHitsQuery,
    useFestivalFilmDetailQuery,
    useFilmDetailQuery,
    FilmDetailQuery,
    FilmDetailQueryVariables,
} from '../../../gql/api';
import FilmProgrammePart from '../../../components/FilmProgrammePart';
import useToggleFilmFavorite from '../../../hooks/useToggleFilmFavorite';
import useToggleCalendar from '../../../hooks/useToggleCalendar';
import { useTheme } from '@emotion/react';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';
import { useSession } from 'next-auth/react';
import Link from 'next/link';

const FestivalFilmDetail = () => {
    const { query, locale, asPath, isFallback } = useRouter();
    const { status } = useSession();

    const vars = {
        id: query.id as string,
        language: locale,
    };
    const { data, isLoading } = useQuery(
        [...useFestivalFilmDetailQuery.getKey(vars), { loggedIn: status === 'authenticated' }],
        useApi<FestivalFilmDetailQuery, FestivalFilmDetailQueryVariables>(FestivalFilmDetailDocument).bind(null, vars),
        { enabled: !!query.id }
    );
    const { data: alertData } = useFilmAlertQuery(
        {
            site: siteHandleByLocale[locale],
        },
        {
            enabled: !isFallback,
        }
    );
    const alert = alertData?.globalSet?.__typename === 'festivalfilmAlert_GlobalSet' && alertData.globalSet;

    const film = data?.film;

    const {
        title,
        translatedTitle,
        meta,
        heroImage,
        ogImage,
        description,
        primaryAward,
        edition,
        trailers,
        films,
        sections,
        mediaImages,
        mediaModalProps,
        shows,
        showDatepicker,
        lengthInMinutes,
    } = useFilmInfo({ film, filmOrProjectType: 'schedule' });
    const credits = useMemo(() => (film ? getCredits(film?.credits, [], false, true) : []), [film]);
    const { videoSrc, handleVideoClick, ...theaterDisclosureProps } = useTheaterModal();
    const [showFullBio, setShowFullBio] = useState(false);
    const programUri = useProgramPathQuery(
        {
            site: siteHandleByLocale[locale],
        },
        { enabled: !isFallback }
    ).data?.entry?.uri;
    const { isFavorited, toggleFavorite, isLoadingFavorites } = useToggleFilmFavorite();
    const { isAddedToCalendar, toggleCalendar, isLoadingCalendar } = useToggleCalendar();
    const intl = useIntl();

    const tickets = useMemo(
        () =>
            shows.map(show => ({
                title: formatShowTitle(show, intl),
                location: show.location,
                accessibility: show.accessibility
                    ?.map(a => a.translation)
                    .filter(Boolean)
                    .join(', '),
                startDate: new Date(show.startOn),
                endDate: new Date(show.endOn),
                ticketType: 'show',
                soldOut: show.ticketAvailabilityStatus === TicketAvailabilityStatusEnum.SoldOut,
                unavailable: show.noSale,
                id: show.id,
                externalTicketLink: show.externalSaleLink,
            })),
        [intl, shows]
    );
    const theme = useTheme();

    useEffect(() => {
        if (film) {
            gtm.pageView(asPath, {
                page_pillar: 'professionals',
                page_navigation: film.fullPreferredTitle,
                page_language: locale,
                film_id: film.id,
                film_name: film.fullPreferredTitle,
                film_edition: film.edition.year.toString(),
                film_section: film.sections?.join(', ') ?? '',
                film_subject: film.subject?.map(s => s.translation).join(', ') ?? '',
            });
        }
    }, [film, asPath, locale]);

    if (isFallback || isLoading) {
        return <LoadingFilmPage />;
    }

    return (
        <>
            <NextSeo
                title={`${title} (${edition.year})`}
                description={striptags(description)}
                titleTemplate="%s | IDFA Festival"
                openGraph={{
                    images: ogImage,
                }}
            />
            <Hero
                image={heroImage}
                topChildren={
                    <FestivalBreadcrumbs
                        parentUri={programUri}
                        currentPage={{
                            title,
                            uri: asPath,
                        }}
                        onImage
                    />
                }
            >
                {film && (
                    <Stack spacing={7}>
                        <Stack spacing={4}>
                            {film.isDocLab && (
                                <Tag color="neutralwhite.">
                                    <FormattedMessage defaultMessage="DocLab" />
                                </Tag>
                            )}
                            <Stack mt={2}>
                                <Heading as="h1" variant={1} clamp={3}>
                                    {title}
                                </Heading>
                                {translatedTitle && <Heading variant={5}>{translatedTitle}</Heading>}
                            </Stack>
                            <Box>
                                <Meta
                                    {...meta}
                                    items={[
                                        ...meta.items,
                                        sections &&
                                            !meta.items.some(item => item.key === 'sections') && {
                                                key: 'sections',
                                                value: sections,
                                            },
                                    ].filter(Boolean)}
                                    size="m"
                                />
                            </Box>
                        </Stack>

                        <HStack spacing={6}>
                            {tickets.length > 0 && (
                                <Button variant="solidOnDark" as="a" href="#tickets">
                                    {tickets.some(t => t.unavailable) ? (
                                        <FormattedMessage defaultMessage="Tijden" />
                                    ) : (
                                        <FormattedMessage defaultMessage="Tickets & Tijden" />
                                    )}
                                </Button>
                            )}
                            <VideoButtons films={films} trailers={trailers ?? []} handleVideoClick={handleVideoClick} />
                            <FavoriteButton
                                borderColor="neutralwhite."
                                iconColor="neutralwhite."
                                isFavorite={isFavorited(film.id)}
                                onClick={() => toggleFavorite(film.id)}
                                isLoading={isLoadingFavorites}
                            />
                        </HStack>
                    </Stack>
                )}
            </Hero>
            {alert && !!alert.filmAlert?.trim() && (
                <Box bg={theme.tokens.SyntaxBackgroundPrimaryDefault}>
                    <Container p={4} sx={{ a: { color: 'inherit !important' } }} textAlign="center">
                        <Box>
                            <Body>
                                <HtmlContent html={alert.filmAlert} />
                            </Body>
                        </Box>
                    </Container>
                </Box>
            )}
            <TheaterModal title={title} meta={{ ...meta }} videoSrc={videoSrc} {...theaterDisclosureProps} />
            {film && (
                <Flex flexDir="column" gap={[9, null, 11]} mt={11}>
                    <Wrapper order={[0, null, 1]} alignItems="center">
                        <Grid gridTemplateRows="min-content 1fr" alignItems="start">
                            <Box gridColumn={['1 / -1', null, null, '1 / span 6', '1 / span 6']}>
                                <Stack spacing={6}>
                                    <Heading variant={3}>
                                        <FormattedMessage defaultMessage="Synopsis" />
                                    </Heading>

                                    {primaryAward && (
                                        <Stack spacing={3}>
                                            <Award mb={3}>{primaryAward.translation}</Award>
                                        </Stack>
                                    )}
                                    <Stack spacing={2}>
                                        <Body
                                            noOfLines={showFullBio ? 0 : 5}
                                            maxHeight={showFullBio ? 'auto' : 'calc(1.75em * 5)'}
                                        >
                                            <HtmlContent
                                                override={{
                                                    p: ({ children, style, ...props }) => (
                                                        <Body
                                                            as="p"
                                                            mb={showFullBio ? 3 : undefined}
                                                            display={showFullBio ? 'block' : 'inline'}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </Body>
                                                    ),
                                                }}
                                                html={description}
                                            />
                                        </Body>
                                        <ColorSchemeProvider colorScheme="neutral">
                                            <TextButton
                                                size="s"
                                                iconRight={
                                                    <Box w={6} h={6}>
                                                        {showFullBio ? <IconRemove /> : <IconAdd />}
                                                    </Box>
                                                }
                                                onClick={() => setShowFullBio(!showFullBio)}
                                                colored
                                            >
                                                {showFullBio ? (
                                                    <FormattedMessage defaultMessage="Lees minder" />
                                                ) : (
                                                    <FormattedMessage defaultMessage="Lees verder" />
                                                )}
                                            </TextButton>
                                        </ColorSchemeProvider>
                                    </Stack>
                                </Stack>
                            </Box>
                            {shows?.length > 0 && (
                                <Box
                                    gridColumn={['1 / -1', null, null, '7 / span 6', '8 / span 5']}
                                    gridRow={[null, null, null, '1 / -1']}
                                    id="tickets"
                                    scrollMarginTop={15}
                                >
                                    {!showDatepicker ? (
                                        <ContentBlockTickets
                                            tickets={tickets}
                                            isAddedToCalendar={isAddedToCalendar}
                                            toggleCalendar={toggleCalendar}
                                            isLoadingCalendar={isLoadingCalendar}
                                            tooltipContent={
                                                <Body>
                                                    <FormattedMessage
                                                        defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                        id="accessibility.tooltip"
                                                        values={{
                                                            a: children => (
                                                                <Link
                                                                    href={
                                                                        locale === 'nl'
                                                                            ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                            : '/en/plan-your-visit/locations-and-accessibility/'
                                                                    }
                                                                >
                                                                    {children}
                                                                </Link>
                                                            ),
                                                        }}
                                                    />
                                                </Body>
                                            }
                                        />
                                    ) : (
                                        <TicketDatePicker
                                            tickets={tickets}
                                            isAddedToCalendar={isAddedToCalendar}
                                            toggleCalendar={toggleCalendar}
                                            isLoadingCalendar={isLoadingCalendar}
                                            tooltipContent={
                                                <Body>
                                                    <FormattedMessage
                                                        defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                        id="accessibility.tooltip"
                                                        values={{
                                                            a: children => (
                                                                <Link
                                                                    href={
                                                                        locale === 'nl'
                                                                            ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                            : '/en/plan-your-visit/locations-and-accessibility/'
                                                                    }
                                                                >
                                                                    {children}
                                                                </Link>
                                                            ),
                                                        }}
                                                    />
                                                </Body>
                                            }
                                        />
                                    )}
                                </Box>
                            )}
                            <Box gridColumn={['1 / -1', null, '1 / span 7', '1 / span 5', '1 / span 6']}>
                                <Stack spacing={11}>
                                    {credits?.length > 0 && (
                                        <Box gridColumn={['1 / -1', null, '1 / span 9', '1 / span 8']}>
                                            <Stack spacing={6}>
                                                <Heading variant={3}>Credits</Heading>
                                                <Meta
                                                    size="m"
                                                    fontWeight="xl"
                                                    items={[
                                                        lengthInMinutes && {
                                                            key: 'Length in minutes',
                                                            value: lengthInMinutes,
                                                        },
                                                        film.colour?.translation && {
                                                            key: 'Color',
                                                            value: film.colour?.translation,
                                                        },
                                                        film.format?.translation && {
                                                            key: 'Format',
                                                            value: film.format?.translation,
                                                        },
                                                        film.spokenLanguages?.length > 0 && {
                                                            key: 'Spoken languages',
                                                            value: `${intl.formatMessage({
                                                                defaultMessage: 'Spoken languages: ',
                                                                id: 'spokenLanguages',
                                                            })} ${film.spokenLanguages
                                                                .map(language => language.translation)
                                                                .join(', ')}`,
                                                        },
                                                    ].filter(Boolean)}
                                                />
                                                <LightTable lines={credits} />
                                            </Stack>
                                        </Box>
                                    )}
                                    {mediaImages.length > 0 && (
                                        <Stack key={`media images: ${film.id}`} as="section" spacing={6}>
                                            <Heading variant={3} as="h2">
                                                <FormattedMessage defaultMessage="Stills" />
                                            </Heading>
                                            <StillsCarousel media={mediaImages.filter(Boolean)} />
                                        </Stack>
                                    )}
                                </Stack>
                                <MediaModal
                                    {...mediaModalProps}
                                    closeLabel={<FormattedMessage defaultMessage="Sluiten" description="Close modal" />}
                                />
                            </Box>
                        </Grid>
                    </Wrapper>
                </Flex>
            )}
            <FilmProgrammePart film={film} />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
    const films = (
        await Promise.all(
            locales.map(
                async locale =>
                    [
                        await fetchApiData<AlphabetSearchHitsQuery, AlphabetSearchHitsQueryVariables>({
                            locale,
                            query: useAlphabetSearchHitsQuery.document,
                            variables: {
                                offset: 0,
                                limit: 100,
                            },
                        }),
                        locale,
                    ] as const
            )
        )
    ).flatMap(([data, locale]) => data.searchFestivalFilms.hits.map(film => [film, locale] as const));

    return {
        paths: films
            .filter(([film]) => !!slugifyTitle(film.fullPreferredTitle))
            .map(([film, locale]) => ({
                params: {
                    id: film.id,
                    slug: slugifyTitle(film.fullPreferredTitle),
                },
                locale,
            })),

        fallback: true,
    };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
        language: locale,
    };
    const data = await queryClient.fetchQuery(
        [...useFestivalFilmDetailQuery.getKey(variables), { loggedIn: false }],
        () =>
            fetchApiData<FestivalFilmDetailQuery, FestivalFilmDetailQueryVariables>({
                locale,
                query: useFestivalFilmDetailQuery.document,
                variables,
            })
    );

    if (!data.film || data.film?.isAlternativeContent) {
        const archiveFilm = await queryClient.fetchQuery(
            [...useFilmDetailQuery.getKey(variables), { loggedIn: false }],
            () =>
                fetchApiData<FilmDetailQuery, FilmDetailQueryVariables>({
                    locale,
                    query: useFilmDetailQuery.document,
                    variables,
                })
        );

        if (archiveFilm.film) {
            return {
                redirect: {
                    destination: `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}${locale === 'en' ? 'en/' : ''}film/${
                        archiveFilm.film.id
                    }/${slugifyTitle(archiveFilm.film.fullPreferredTitle)}`,
                    permanent: false,
                },
            };
        }
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    const filmAlertVariables: FilmAlertQueryVariables = {
        site: siteHandleByLocale[locale],
    };

    await Promise.all([
        queryClient.prefetchQuery(useFilmAlertQuery.getKey(filmAlertVariables), () =>
            fetchCraftData<FilmAlertQuery, FilmAlertQueryVariables>({
                query: useFilmAlertQuery.document,
                variables: filmAlertVariables,
            })
        ),
        (async () => {
            const programUri = await fetchProgramUri(queryClient, locale);
            await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: programUri });
        })(),
    ]);

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default FestivalFilmDetail;
